/**
 * 初期選択画面（3択：プリセットアイコン / プロフィールから生成 / 素材）
 *
 * 選択カードはタブと同様のスタイルを使用：
 * - hover: 不使用（スマホ向け）
 * - transition: 不使用（反応が遅く感じる）
 * - btnDelay: 使用（タップ反応を自然にする）
 */

import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";

interface InitialSelectionProps {
  /** プリセットアイコン選択時のコールバック */
  onSelectPreset: () => void;
  /** プロフィールAI生成選択時のコールバック */
  onSelectProfileAI: () => void;
  /** 素材選択時のコールバック */
  onSelectMaterial: () => void;
  /** 素材選択が利用可能か */
  canUseMaterial?: boolean;
  /** プリセット選択が利用可能か */
  canUsePreset?: boolean;
}

/** 選択カードの設定 */
const SELECTION_OPTIONS = [
  {
    key: "preset",
    icon: "grid_view",
    labelKey: "MSG_ICON_SELECT_SYSTEM",
    descKey: "MSG_ICON_SELECT_SYSTEM_DESC",
    bgColor: "bg-orange-100",
    requiresPreset: true,
    requiresMaterial: false,
  },
  {
    key: "profileAI",
    icon: "auto_awesome",
    labelKey: "MSG_ICON_SELECT_PROFILE_AI",
    descKey: "MSG_ICON_SELECT_PROFILE_AI_DESC",
    bgColor: "bg-blue-100",
    requiresPreset: false,
    requiresMaterial: false,
  },
  {
    key: "material",
    icon: "shopping_bag",
    labelKey: "MSG_ICON_SELECT_MATERIAL",
    descKey: "MSG_ICON_SELECT_MATERIAL_DESC",
    bgColor: "bg-brown-100",
    requiresPreset: false,
    requiresMaterial: true,
  },
] as const;

export const InitialSelection = ({
  onSelectPreset,
  onSelectProfileAI,
  onSelectMaterial,
  canUseMaterial = true,
  canUsePreset = true,
}: InitialSelectionProps) => {
  const play = usePlaySound();

  const handleClick = (key: string) => {
    play();
    btnDelay(() => {
      switch (key) {
        case "preset":
          onSelectPreset();
          break;
        case "profileAI":
          onSelectProfileAI();
          break;
        case "material":
          onSelectMaterial();
          break;
      }
    });
  };

  const visibleOptions = SELECTION_OPTIONS.filter(
    (option) =>
      (!option.requiresPreset || canUsePreset) && (!option.requiresMaterial || canUseMaterial),
  );

  return (
    <div className="flex-col-el flex-center w-full h-full">
      {/* タイトル */}
      <div className="flex-col-el flex-center mb-8">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_ICON_SELECT_TITLE")}
        </p>
      </div>

      {/* 選択カード */}
      <div className="flex-row-el flex-center gap-4">
        {visibleOptions.map((option) => (
          <div
            key={option.key}
            onClick={() => handleClick(option.key)}
            className={`
              flex-col-el items-center justify-center
              w-[220px] h-[220px] rounded-[4px]
              ${option.bgColor}
              cursor-pointer select-none
              active:scale-95
            `}
          >
            <span
              className={`material-symbols-outlined !text-5xl text-white mb-3 ${option.key === "profileAI" ? "mt-8" : ""}`}
            >
              {option.icon}
            </span>
            <p className="text-body text-[22px] text-white text-center whitespace-pre-line">
              {I18n.t(option.labelKey)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
